import { supabase } from '../lib/supabase';

/**
 * Highlight Service — manages verse highlights persisted to Supabase.
 * Highlights are full-verse only, with 5 color options.
 */

export const HIGHLIGHT_COLORS = [
  { id: 'yellow', label: 'Yellow', bg: 'rgba(253, 224, 71, 0.35)', border: 'rgba(234, 179, 8, 0.6)', text: '#713f12' },
  { id: 'green',  label: 'Green',  bg: 'rgba(134, 239, 172, 0.35)', border: 'rgba(34, 197, 94, 0.6)', text: '#14532d' },
  { id: 'blue',   label: 'Blue',   bg: 'rgba(147, 197, 253, 0.35)', border: 'rgba(59, 130, 246, 0.6)', text: '#1e3a5f' },
  { id: 'red',    label: 'Red',    bg: 'rgba(252, 165, 165, 0.35)', border: 'rgba(239, 68, 68, 0.6)',  text: '#7f1d1d' },
  { id: 'pink',   label: 'Pink',   bg: 'rgba(249, 168, 212, 0.35)', border: 'rgba(236, 72, 153, 0.6)', text: '#831843' },
];

export const getHighlightStyle = (colorId) => {
  const color = HIGHLIGHT_COLORS?.find(c => c?.id === colorId);
  if (!color) return null;
  return { backgroundColor: color?.bg, borderColor: color?.border };
};

/**
 * Load all highlights for a user in a specific chapter.
 * Returns a Map<verseNumber, { id, color }>
 */
export const loadChapterHighlights = async (userId, book, chapter) => {
  if (!userId || !book || !chapter) return { data: new Map(), error: null };

  const { data, error } = await supabase?.from('verse_highlights')?.select('id, verse_number, color')?.eq('user_id', userId)?.eq('book', book)?.eq('chapter', chapter);

  if (error) return { data: new Map(), error };

  const map = new Map();
  data?.forEach(row => {
    map?.set(row?.verse_number, { id: row?.id, color: row?.color });
  });

  return { data: map, error: null };
};

/**
 * Add or update a highlight for a verse.
 * Uses upsert so changing color on an existing highlight works.
 */
export const upsertHighlight = async (userId, book, chapter, verseNumber, color) => {
  if (!userId) return { data: null, error: new Error('Not authenticated') };

  const { data, error } = await supabase?.from('verse_highlights')?.upsert(
      {
        user_id: userId,
        book,
        chapter,
        verse_number: verseNumber,
        color,
        updated_at: new Date()?.toISOString(),
      },
      { onConflict: 'user_id,book,chapter,verse_number' }
    )?.select('id, verse_number, color')?.single();

  return { data, error };
};

/**
 * Remove a highlight by its database id.
 */
export const removeHighlight = async (highlightId) => {
  const { error } = await supabase?.from('verse_highlights')?.delete()?.eq('id', highlightId);

  return { error };
};

/**
 * Count total highlights for a user (for community toggle counter).
 */
export const countUserHighlights = async (userId) => {
  if (!userId) return { count: 0, error: null };

  const { count, error } = await supabase?.from('verse_highlights')?.select('id', { count: 'exact', head: true })?.eq('user_id', userId);

  return { count: count || 0, error };
};
